import React from "react";
import Link from "next/link";

interface TextLinkBaseProps {
  /** Link label */
  children: React.ReactNode;
  /** Show the arrow icon (↗) */
  arrow?: boolean;
  /**
   * Typography size variant.
   * - `"base"` (default) — 16px Semibold (`text-link`)
   * - `"lg"` — 20px Bold (`text-heading-thin`), for content title links
   * - `"caption"` — 12px Bold (`text-labels-captions-bold`), for compact/sidebar links
   */
  size?: "base" | "lg" | "caption";
  /** Additional class overrides (e.g. text-error) */
  className?: string;
}

type TextLinkProps = TextLinkBaseProps &
  (
    | ({ as?: "button" } & React.ButtonHTMLAttributes<HTMLButtonElement>)
    | ({ as: "a"; href: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">)
  );

const sizeStyles: Record<"base" | "lg" | "caption", string> = {
  base: "text-link",
  lg: "text-heading-thin",
  caption: "text-labels-captions-bold",
};

const baseStyles =
  "inline-flex items-center gap-1 text-charcoal hover:underline transition-colors";

const Arrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 7h10v10" />
    <path d="M7 17 17 7" />
  </svg>
);

/**
 * @component TextLink
 * @description Tertiary / Text link from the GFS design system.
 * Arquitecta semibold, base size, hover underline, optional arrow icon.
 * - Default (`as="button"`) — renders a `<button>` for click callbacks
 * - `as="a"` — renders a Next.js `<Link>` (handles both internal and external URLs)
 *
 * Size variants:
 * - `"base"` (default) — 16px Semibold (`text-link`)
 * - `"lg"` — 20px Bold (`text-heading-thin`), for content title links
 * - `"caption"` — 12px Bold (`text-labels-captions-bold`), for compact/sidebar links
 *
 * @example
 * <TextLink onClick={handleClick}>Forgot Password</TextLink>
 * <TextLink as="a" href="/visits" arrow>See All Visits</TextLink>
 * <TextLink as="a" href="https://example.com" size="caption" arrow>Renew Now</TextLink>
 */
export default function TextLink({
  children,
  arrow = false,
  size = "base",
  className = "",
  as: componentAs,
  ...rest
}: TextLinkProps) {
  const classes = `${baseStyles} ${sizeStyles[size]} ${className}`.trim();

  if (componentAs === "a") {
    const { href, ...linkProps } =
      rest as { href: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">;
    return (
      <Link href={href} className={classes} {...linkProps}>
        {children}
        {arrow && <Arrow />}
      </Link>
    );
  }

  return (
    <button type="button" className={classes} {...rest as React.ButtonHTMLAttributes<HTMLButtonElement>}>
      {children}
      {arrow && <Arrow />}
    </button>
  );
}
